import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, RefreshCw, Smartphone } from 'lucide-react';
import {
  buildCommunicationMessageFormData,
  fetchDriverCustomerChannelStatus,
  fetchConversationMessages,
  hasCommunicationMedia,
  markConversationRead,
  sendConversationMessage,
  sendDriverCustomerMessage,
} from './api';
import { socket, socketAuthPayload } from '../../admin/chat/socket';
import DeliverySlotInlineSelector from './DeliverySlotInlineSelector';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';

export default function ConversationThread({
  conversationId,
  currentUserId,
  currentUserType = 'admin',
  className = '',
  showInput = true,
  allowNotes = true,
  inputPlaceholder = 'Escribe un mensaje interno...',
  inputHelperText,
  onConversationLoaded,
  enableDriverCustomerChannels = false,
  enableFinalCustomerWhatsApp = false,
  enableDeliverySlotSelection = false,
}) {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [driverCustomerChannel, setDriverCustomerChannel] = useState(
    currentUserType === 'driver' ? 'fastmax_app' : 'internal'
  );
  const [driverCustomerStatus, setDriverCustomerStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const driverCustomerEnabled = (
    enableDriverCustomerChannels
    && currentUserType === 'driver'
    && conversation?.type === 'delivery'
    && conversation?.delivery_point_id
  );
  const finalCustomerWhatsAppEnabled = (
    enableFinalCustomerWhatsApp
    && ['store', 'provider'].includes(currentUserType)
    && conversation?.type === 'delivery'
    && conversation?.delivery_point_id
  );
  const deliverySlotSelectionEnabled = (
    enableDeliverySlotSelection
    && ['store', 'provider'].includes(currentUserType)
    && conversation?.type === 'delivery'
    && conversation?.delivery_point_id
  );
  const deliveryCustomerStatusEnabled = driverCustomerEnabled || finalCustomerWhatsAppEnabled;
  const driverWhatsAppAvailable = Boolean(driverCustomerStatus?.whatsapp_available);
  const rawWhatsAppDisabledReason = driverCustomerStatus?.whatsapp_disabled_reason || (
    currentUserType === 'driver'
      ? 'WhatsApp no disponible. Usa Cliente App.'
      : 'WhatsApp no disponible. Usa Interno Fast Max.'
  );
  const driverWhatsAppDisabledReason = currentUserType === 'driver'
    ? rawWhatsAppDisabledReason
    : rawWhatsAppDisabledReason.replace('Usa Cliente App.', 'Usa Interno Fast Max.');

  const canUseDeliveryCustomerChannels = useCallback((nextConversation) => (
    nextConversation?.type === 'delivery'
    && nextConversation?.delivery_point_id
    && (
      (enableDriverCustomerChannels && currentUserType === 'driver')
      || (enableFinalCustomerWhatsApp && ['store', 'provider'].includes(currentUserType))
    )
  ), [currentUserType, enableDriverCustomerChannels, enableFinalCustomerWhatsApp]);

  const loadMessages = useCallback(async () => {
    if (!conversationId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchConversationMessages(conversationId, {
        currentUserType,
        currentUserId,
      });
      setConversation(data.conversation);
      onConversationLoaded?.(data.conversation);
      if (canUseDeliveryCustomerChannels(data.conversation)) {
        const status = await fetchDriverCustomerChannelStatus(conversationId);
        setDriverCustomerStatus(status || null);
        if (!status?.whatsapp_available) {
          setDriverCustomerChannel(currentUserType === 'driver' ? 'fastmax_app' : 'internal');
        }
      } else {
        setDriverCustomerStatus(null);
        setDriverCustomerChannel(currentUserType === 'driver' ? 'fastmax_app' : 'internal');
      }
      setMessages(data.messages || []);
      await markConversationRead(conversationId, {
        recipient_type: currentUserType,
        user_id: currentUserId,
        driver_id: currentUserType === 'driver' ? currentUserId : undefined,
      });
    } catch (err) {
      console.error('Communication thread error:', err);
      setError('No se pudo cargar la conversación');
    } finally {
      setIsLoading(false);
    }
  }, [canUseDeliveryCustomerChannels, conversationId, currentUserId, currentUserType, onConversationLoaded]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    setDriverCustomerChannel(currentUserType === 'driver' ? 'fastmax_app' : 'internal');
    setDriverCustomerStatus(null);
    setError(null);
  }, [conversationId, currentUserType]);

  useEffect(() => {
    if (!conversationId) return undefined;

    socket.emit('join_conversation', socketAuthPayload({ conversation_id: conversationId }));
    const handleMessage = (message) => {
      const payloadMessage = message?.message || message;
      if (Number(payloadMessage?.conversation_id) !== Number(conversationId)) return;
      if (!payloadMessage?.id) return;
      setMessages(prev => {
        if (prev.some(item => item.id === payloadMessage.id && item.channel === payloadMessage.channel)) return prev;
        return [...prev, payloadMessage];
      });
    };
    const handleConversationUpdated = (payload) => {
      if (Number(payload?.conversation_id) !== Number(conversationId)) return;
      if (payload.conversation) {
        setConversation(payload.conversation);
        onConversationLoaded?.(payload.conversation);
      }
    };
    const handleReadUpdated = (payload) => {
      if (Number(payload?.conversation_id) !== Number(conversationId)) return;
      if (payload.conversation) {
        setConversation(payload.conversation);
        onConversationLoaded?.(payload.conversation);
      }
    };
    const handleConnect = () => {
      socket.emit('join_conversation', socketAuthPayload({ conversation_id: conversationId }));
      loadMessages();
    };

    socket.on('conversation_message_created', handleMessage);
    socket.on('conversation_updated', handleConversationUpdated);
    socket.on('conversation_read_updated', handleReadUpdated);
    socket.on('connect', handleConnect);

    return () => {
      socket.off('conversation_message_created', handleMessage);
      socket.off('conversation_updated', handleConversationUpdated);
      socket.off('conversation_read_updated', handleReadUpdated);
      socket.off('connect', handleConnect);
      socket.emit('leave_conversation', socketAuthPayload({ conversation_id: conversationId }));
    };
  }, [conversationId, loadMessages, onConversationLoaded]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleDeliverySlotUpdated = useCallback((deliveryPoint) => {
    if (!deliveryPoint?.id) return;
    setConversation(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        delivery: {
          ...(prev.delivery || {}),
          ...deliveryPoint,
        },
      };
    });
  }, []);

  async function handleSend(payload) {
    let message;
    try {
      if (driverCustomerEnabled) {
        const selectedChannel = driverCustomerChannel === 'whatsapp' && driverWhatsAppAvailable
          ? 'whatsapp'
          : 'fastmax_app';
        const requestPayload = hasCommunicationMedia(payload)
          ? buildCommunicationMessageFormData({
              ...payload,
              channel: selectedChannel,
            })
          : {
              content: payload.content,
              channel: selectedChannel,
            };
        const result = await sendDriverCustomerMessage(conversationId, requestPayload);
        message = result.message;
        if (selectedChannel === 'whatsapp') {
          const status = await fetchDriverCustomerChannelStatus(conversationId);
          setDriverCustomerStatus(status || null);
        }
      } else if (finalCustomerWhatsAppEnabled && driverCustomerChannel === 'whatsapp') {
        if (!driverWhatsAppAvailable) {
          throw new Error(driverWhatsAppDisabledReason);
        }
        const requestPayload = hasCommunicationMedia(payload)
          ? buildCommunicationMessageFormData({
              ...payload,
              channel: 'whatsapp',
            })
          : {
              content: payload.content,
              channel: 'whatsapp',
            };
        const result = await sendDriverCustomerMessage(conversationId, requestPayload);
        message = result.message;
        const status = await fetchDriverCustomerChannelStatus(conversationId);
        setDriverCustomerStatus(status || null);
      } else {
        const requestPayload = {
          ...payload,
          sender_type: currentUserType,
          sender_user_id: currentUserId,
          sender_driver_id: currentUserType === 'driver' ? currentUserId : undefined,
        };
        message = await sendConversationMessage(
          conversationId,
          hasCommunicationMedia(payload) ? buildCommunicationMessageFormData(requestPayload) : requestPayload
        );
      }
    } catch (err) {
      const status = err?.response?.status;
      const messageText = err?.response?.data?.message || err?.response?.data?.error || err.message || 'No se pudo enviar el mensaje.';
      if (status === 428 && deliveryCustomerStatusEnabled) {
        setDriverCustomerChannel(currentUserType === 'driver' ? 'fastmax_app' : 'internal');
        setDriverCustomerStatus(prev => ({
          ...(prev || {}),
          whatsapp_available: false,
          whatsapp_disabled_reason: messageText,
        }));
      }
      setError(messageText);
      throw err;
    }
    if (!message) return;
    setMessages(prev => {
      if (prev.some(item => item.id === message.id && item.channel === message.channel)) return prev;
      return [...prev, message];
    });
  }

  if (!conversationId) {
    return (
      <div className={`flex items-center justify-center bg-slate-50 text-slate-500 ${className}`}>
        Selecciona una conversación
      </div>
    );
  }

  return (
    <div className={`flex h-full min-h-[360px] flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50 sm:min-h-[420px] ${className}`}>
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-3 sm:px-4">
        <div className="min-w-0">
          <h3 className="text-sm font-extrabold text-slate-900 font-montserrat">
            Comunicación Fast Max
          </h3>
          <p className="truncate text-xs text-slate-500 font-montserrat">
            {conversation?.delivery_point_id ? `Entrega #${conversation.delivery_point_id}` : `Conversación #${conversationId}`}
          </p>
        </div>
        <button
          onClick={loadMessages}
          disabled={isLoading}
          className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-50 sm:py-1.5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {deliverySlotSelectionEnabled && (
        <DeliverySlotInlineSelector
          conversationId={conversationId}
          conversation={conversation}
          onSlotUpdated={handleDeliverySlotUpdated}
        />
      )}

      <div className="flex-1 space-y-3 overflow-y-auto p-3 sm:p-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {!error && messages.length === 0 && !isLoading && (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
            Sin mensajes todavía. Usa este hilo para centralizar notas, WhatsApp y coordinación interna.
          </div>
        )}

        {messages.map(message => (
          <MessageBubble key={`${message.channel}-${message.id}`} message={message} currentUserType={currentUserType} />
        ))}
        <div ref={bottomRef} />
      </div>

      {showInput && (
        <>
          {driverCustomerEnabled && (
            <div className="border-t border-slate-200 bg-white px-2 py-2 sm:px-3">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDriverCustomerChannel('fastmax_app')}
                  className={`inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold sm:flex-none ${
                    driverCustomerChannel === 'fastmax_app' ? 'bg-[#B1336E] text-white' : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  <FileText className="h-4 w-4" />
                  Cliente App
                </button>
                <button
                  type="button"
                  onClick={() => driverWhatsAppAvailable && setDriverCustomerChannel('whatsapp')}
                  disabled={!driverWhatsAppAvailable}
                  className={`inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none ${
                    driverCustomerChannel === 'whatsapp' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700'
                  }`}
                  title={driverWhatsAppAvailable ? 'Enviar por WhatsApp' : driverWhatsAppDisabledReason}
                >
                  <Smartphone className="h-4 w-4" />
                  WhatsApp
                </button>
                {!driverWhatsAppAvailable && (
                  <span className="text-[11px] font-semibold text-amber-700">{driverWhatsAppDisabledReason}</span>
                )}
              </div>
            </div>
          )}
          {finalCustomerWhatsAppEnabled && (
            <div className="border-t border-slate-200 bg-white px-2 py-2 sm:px-3">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDriverCustomerChannel('internal')}
                  className={`inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold sm:flex-none ${
                    driverCustomerChannel === 'internal' ? 'bg-[#B1336E] text-white' : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  <FileText className="h-4 w-4" />
                  Interno Fast Max
                </button>
                <button
                  type="button"
                  onClick={() => driverWhatsAppAvailable && setDriverCustomerChannel('whatsapp')}
                  disabled={!driverWhatsAppAvailable}
                  className={`inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none ${
                    driverCustomerChannel === 'whatsapp' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700'
                  }`}
                  title={driverWhatsAppAvailable ? 'Enviar al WhatsApp del cliente final' : driverWhatsAppDisabledReason}
                >
                  <Smartphone className="h-4 w-4" />
                  WhatsApp cliente
                </button>
                {!driverWhatsAppAvailable && (
                  <span className="text-[11px] font-semibold text-amber-700">{driverWhatsAppDisabledReason}</span>
                )}
              </div>
            </div>
          )}
          <MessageInput
            onSend={handleSend}
            disabled={isLoading}
            allowNotes={driverCustomerEnabled ? false : allowNotes}
            placeholder={driverCustomerEnabled
              ? (driverCustomerChannel === 'whatsapp' ? 'Escribe al WhatsApp del cliente...' : 'Escribe al cliente final...')
              : finalCustomerWhatsAppEnabled && driverCustomerChannel === 'whatsapp'
                ? 'Escribe al WhatsApp del cliente final...'
                : inputPlaceholder}
            helperText={driverCustomerEnabled
              ? (driverCustomerChannel === 'whatsapp'
                  ? 'Mensaje libre al WhatsApp del cliente final. Solo disponible con ventana de 24h abierta.'
                  : 'Mensaje visible para el cliente final dentro de Fast Max App y registrado en esta entrega.')
              : finalCustomerWhatsAppEnabled && driverCustomerChannel === 'whatsapp'
                ? 'Mensaje libre al WhatsApp del cliente final. Solo disponible con ventana de 24h abierta y queda registrado en esta entrega.'
                : inputHelperText}
          />
        </>
      )}
    </div>
  );
}
